import { useLeads } from '@/leads/LeadsProvider';
import { aplicarFiltros, ehIntervalo, janelaDoPeriodo, mesesComLeads, partesDoIntervalo, periodoDeIntervalo, rotuloDoMes, temFiltroAtivo } from '@/leads/filtros';

const rotuloDoPadrao = (periodo) => rotuloDoMes(periodo);
import { ETAPAS_PADRAO, TEMPERATURAS } from '@/lib/constants';
import { SEM_ORIGEM, origensAtivas } from '@/leads/origens';
import { CAMPO_MOTIVO, ETAPAS_COM_MOTIVO, listaDeMotivos, motivoPorId } from '@/leads/motivos';
import { etapaPorId } from '@/lib/constants';
import MultiSelect from '@/components/MultiSelect';
import { apenasNegociadores, apenasSdrs } from '@/equipe/funcoes';
import { contarAparicoes } from '@/leads/resgate';
import AvisoCarga from '@/components/AvisoCarga';

/**
 * ★ VJ — as opções do filtro "Motivo": por coluna, os motivos ATIVOS da config
 * ∪ todo id GRAVADO em algum lead (inativo continua legível, como na VG); um
 * inativo que nenhum lead tem não aparece. Rótulo por `motivoPorId`.
 */
export function gruposDeMotivos(config, leads = []) {
  return ETAPAS_COM_MOTIVO.map((etapa) => {
    const campo = CAMPO_MOTIVO[etapa];
    const gravados = new Set(leads.map((l) => l?.[campo]).filter(Boolean));
    const lista = listaDeMotivos(config, etapa);
    const opcoes = lista
      .filter((m) => m.ativo || gravados.has(m.id))
      .map((m) => ({ id: m.id, rotulo: m.rotulo }));
    for (const id of gravados) {
      if (!lista.some((m) => m.id === id)) opcoes.push({ id, rotulo: motivoPorId(config, etapa, id).rotulo });
    }
    return { id: etapa, rotulo: etapaPorId(etapa).label, opcoes };
  });
}

/**
 * Filtros combinados compartilhados pelo kanban e pela lista (PLANO 4.4).
 *
 * ★ PC-4 (decisões do dono, 18/out/2026 — `docs/medicao-pc4-filtro-funil.md` §5):
 *  - `opcaoEncerrados`: "Mostrar encerrados" só na Lista — no Funil ele não mudava o quadro
 *    (os encerrados têm coluna própria), só o contador. Mesmo padrão do `mostrarEtapa`.
 *  - `filtrados`: o que a TELA mostra, para o "X de Y". ⚠ O `leadsFiltrados` do provider
 *    exclui encerrados e não conhece as tarefas — no Funil ele contava menos que o quadro.
 *  - `tarefas`: `{ comAberta, estado }` de `useTarefasAbertas`. Sem `comAberta` o filtro
 *    "Sem próximo contato" não é aplicado, e a barra diz por quê.
 *  - `atalhoSemProximo`: o "⚑ N sem próximo contato" (só o Funil), com N respeitando os
 *    outros filtros da barra; some com N = 0 e enquanto as tarefas não são confiáveis.
 */
export default function FiltrosBarra({ mostrarEtapa = true, opcaoEncerrados = true, filtrados = null, tarefas = null, atalhoSemProximo = false }) {
  const { filtros, setFiltros, limparFiltros, equipe, leads, leadsFiltrados, clinica, filtrosIniciais: padrao } = useLeads();
  const naTela = filtrados ?? leadsFiltrados;
  // VL: meses derivados dos dados (nunca uma lista fixa); o mês do padrão entra mesmo sem lead ainda.
  const meses = mesesComLeads(leads);
  const mesPadrao = padrao?.periodo ?? '';
  const mesAtualNaLista = meses.some((m) => m.valor === mesPadrao);
  const intervalo = partesDoIntervalo(filtros.periodo);
  const janela = janelaDoPeriodo(filtros.periodo);
  const MAIS = [
    // ★ PC-4: Não agendou / Não compareceu / Não fechou sem nenhuma tarefa aberta, de ninguém.
    { id: 'semProximoContato', rotulo: 'Sem próximo contato' },
    // VQ-1: encerrados ficam fora por padrão. ★ PC-4: só na Lista (no Funil, a coluna recolhida).
    ...(opcaoEncerrados ? [{ id: 'mostrarEncerrados', rotulo: 'Mostrar encerrados' }] : []),
  ];
  const comAberta = tarefas?.comAberta ?? null;
  const semProximoSemDado = filtros.semProximoContato && !comAberta;
  const nSemProximo = atalhoSemProximo && comAberta && !filtros.semProximoContato
    ? aplicarFiltros(leads, { ...filtros, mostrarEncerrados: true, semProximoContato: true }, { comAberta }).length
    : 0;
  // ★ No Funil "Mostrar encerrados" não existe: um valor herdado da Lista não acende "Limpar filtros".
  const paraAtivo = opcaoEncerrados ? filtros : { ...filtros, mostrarEncerrados: padrao?.mostrarEncerrados ?? false };
  const mudar = (campo) => (e) => {
    const valor = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFiltros((f) => ({ ...f, [campo]: valor }));
  };

  return (
    <>
    <div className="filtros">
      <input
        type="search"
        className="filtro-busca"
        placeholder="Buscar por nome, telefone, e-mail…"
        value={filtros.busca}
        onChange={mudar('busca')}
        aria-label="Buscar leads"
      />

      {mostrarEtapa && (
        <select value={filtros.etapa} onChange={mudar('etapa')} aria-label="Etapa">
          <option value="">Todas as etapas</option>
          {ETAPAS_PADRAO.map((e) => (
            <option key={e.id} value={e.id}>
              {e.label}
            </option>
          ))}
        </select>
      )}

      <select value={filtros.origem} onChange={mudar('origem')} aria-label="Origem">
        <option value="">Todas as origens</option>
        {/* VH-1: as ativas da clínica, na ordem da config, e "Sem origem" — que casa vazio,
            ausente e LEGADO. É como a SDR acha quem ainda precisa de origem. */}
        {origensAtivas(clinica?.config).map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
        <option value={SEM_ORIGEM}>Sem origem</option>
      </select>

      {/* VJ: motivo de Não agendou / Não fechou, múltipla escolha (OU), independente da etapa. */}
      <MultiSelect
        rotulo="Motivo"
        unidade="motivo"
        grupos={gruposDeMotivos(clinica?.config, leads)}
        selecionados={filtros.motivos ?? []}
        aoMudar={(ids) => setFiltros((f) => ({ ...f, motivos: ids }))}
      />

      <select value={filtros.sdrId} onChange={mudar('sdrId')} aria-label="SDR responsável">
        <option value="">Todos os SDRs</option>
        {apenasSdrs(equipe).map((u) => (
          <option key={u.id} value={u.id}>
            {u.nome}
          </option>
        ))}
      </select>

      <select value={filtros.negociadorId} onChange={mudar('negociadorId')} aria-label="Negociador">
        <option value="">Todos os negociadores</option>
        {apenasNegociadores(equipe).map((u) => (
          <option key={u.id} value={u.id}>
            {u.nome}
          </option>
        ))}
      </select>

      <select value={filtros.temperatura} onChange={mudar('temperatura')} aria-label="Temperatura">
        <option value="">Qualquer temperatura</option>
        {TEMPERATURAS.map((t) => (
          <option key={t.id} value={t.id}>
            {t.label}
          </option>
        ))}
      </select>

      {/* VL: período SEMPRE por criadoEm — o "1º contato", o mesmo nome da coluna da lista. */}
      <fieldset className="filtro-periodo">
        <select
          value={ehIntervalo(filtros.periodo) ? 'intervalo' : filtros.periodo}
          onChange={(e) => {
            const v = e.target.value;
            setFiltros((f) => ({ ...f, periodo: v === 'intervalo' ? periodoDeIntervalo('', '') : v }));
          }}
          aria-label="1º contato"
        >
          <option value="">1º contato: todo o período</option>
          <optgroup label="Recentes">
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="90d">Últimos 90 dias</option>
          </optgroup>
          <optgroup label="Mês do 1º contato">
            {!mesAtualNaLista && mesPadrao && <option value={mesPadrao}>{rotuloDoPadrao(mesPadrao)}</option>}
            {meses.map((m) => (
              <option key={m.valor} value={m.valor}>{m.rotulo}</option>
            ))}
          </optgroup>
          <option value="intervalo">Intervalo…</option>
        </select>
        {ehIntervalo(filtros.periodo) && (
          <>
            <label className="campo-inline" htmlFor="filtro-de">de
              <input id="filtro-de" type="date" value={intervalo.de}
                onChange={(e) => setFiltros((f) => ({ ...f, periodo: periodoDeIntervalo(e.target.value, partesDoIntervalo(f.periodo).ate) }))} />
            </label>
            <label className="campo-inline" htmlFor="filtro-ate">até
              <input id="filtro-ate" type="date" value={intervalo.ate}
                onChange={(e) => setFiltros((f) => ({ ...f, periodo: periodoDeIntervalo(partesDoIntervalo(f.periodo).de, e.target.value) }))} />
            </label>
            {janela?.invertido && (
              <span className="filtro-aviso" role="status">“de” e “até” estavam trocados — o recorte usa o intervalo certo.</span>
            )}
          </>
        )}
      </fieldset>

      {/* VL: os checkboxes moram em "Mais filtros ▾" (o MultiSelect da VJ) — a barra cabe numa linha. */}
      <MultiSelect
        rotulo="Mais filtros"
        unidade="filtro"
        grupos={[{ id: 'mais', rotulo: 'Mais filtros', opcoes: MAIS }]}
        selecionados={MAIS.filter((o) => filtros[o.id]).map((o) => o.id)}
        aoMudar={(ids) => setFiltros((f) => ({ ...f, ...Object.fromEntries(MAIS.map((o) => [o.id, ids.includes(o.id)])) }))}
      />

      <span className="filtro-contagem">
        {naTela.length} de {leads.length}
        {/* VR: com período, o rodapé diz quantos são novos e quantos resgates. */}
        {janela && (() => { const { novos, resgates } = contarAparicoes(naTela, janela); return ` (${novos} ${novos === 1 ? 'novo' : 'novos'} + ${resgates} ${resgates === 1 ? 'resgate' : 'resgates'})`; })()}
      </span>

      {nSemProximo > 0 && (
        <button type="button" className="btn-texto filtro-atalho" onClick={() => setFiltros((f) => ({ ...f, semProximoContato: true }))}>
          ⚑ {nSemProximo} sem próximo contato
        </button>
      )}

      {temFiltroAtivo(paraAtivo, padrao) && (
        <button type="button" className="btn-texto" onClick={limparFiltros}>
          Limpar filtros
        </button>
      )}
    </div>
    {/* ★ PC-4: filtro ligado sem tarefas confiáveis → avisa e NÃO filtra (fragmento: nenhum elemento a mais sem aviso). */}
    {semProximoSemDado && (tarefas?.estado === 'falhou'
      ? <AvisoCarga oQue="os próximos contatos — o filtro “Sem próximo contato” não foi aplicado" />
      : <p className="aviso-sem-proximo" role="status">Conferindo os próximos contatos com o servidor — o filtro “Sem próximo contato” ainda não foi aplicado.</p>)}
    </>
  );
}
